import { get } from 'lodash/fp';
import { TimeoutError } from '@mews-framework/errors';
import { TrackingConsent } from '@mews/enums';
import { loadScript, retryPromise } from '../../utils';
import createConsentsObservable from './consentsObservable';
import {
    ConsentsObserver,
    CookieFirst as ICookieFirst,
    TrackingConsents,
    TrackingConsentsServiceInitializationValues,
} from './types';

const LOAD_TIMEOUT = 8000;
const LOAD_TRY_INTERVAL = 200;
const COOKIE_FIRST_SCRIPT_URL = 'https://consent.cookiefirst.com/banner.js';
export const DEFAULT_CONSENTS: TrackingConsents = {
    [TrackingConsent.Advertising]: false,
    [TrackingConsent.Functional]: false,
    [TrackingConsent.Necessary]: true,
    [TrackingConsent.Performance]: false,
};

// CookieFirst doesn't have an api for ready status, so we check it indirectly
const isCookieFirstReady = (cookieFirst?: Partial<ICookieFirst>): cookieFirst is ICookieFirst =>
    Boolean(cookieFirst && cookieFirst.openPanel);

interface CreateTrackingConsentServiceParams {
    context?: Window;
}

const createTrackingConsentService = ({ context = window }: CreateTrackingConsentServiceParams = {}) => {
    const consentsObservable = createConsentsObservable(context);
    let initializationPromise: Promise<TrackingConsentsServiceInitializationValues> | null = null;
    let defaultConsents: TrackingConsents = DEFAULT_CONSENTS;

    const getCookieFirstInstance = () => {
        const { CookieFirst } = context;
        return isCookieFirstReady(CookieFirst) ? CookieFirst : undefined;
    };

    const getReadyTime = async (): Promise<number> =>
        new Promise((resolve, reject) => {
            const startTime = window.performance.now();

            const interval = window.setInterval(() => {
                const timeDifference = Math.floor(window.performance.now() - startTime);

                if (getCookieFirstInstance()) {
                    window.clearInterval(interval);
                    resolve(timeDifference);
                }

                if (timeDifference >= LOAD_TIMEOUT) {
                    window.clearInterval(interval);
                    reject(
                        new TimeoutError(
                            'TrackingConsentsService was not initialized. CookieFirst does not exist in global window.',
                            timeDifference
                        )
                    );
                }
            }, LOAD_TRY_INTERVAL);
        });

    const waitScriptToLoad = async (apiKey: string) => {
        await retryPromise(async () => loadScript(COOKIE_FIRST_SCRIPT_URL, { cookiefirstKey: apiKey }, context));

        const processTime = await getReadyTime();

        return { isAlreadyInitialized: false, processTime };
    };

    const getAllConsents = (): TrackingConsents => {
        const cookieFirstInstance = getCookieFirstInstance();
        if (cookieFirstInstance) {
            return cookieFirstInstance.consent || defaultConsents;
        }

        return defaultConsents;
    };

    return {
        initAndOpenBanner: async (apiKey: string): Promise<TrackingConsentsServiceInitializationValues> => {
            if (context.CookieFirst) {
                // This condition prevents cookie first to attach multiple times to a window in mode of hot reload.
                return { isAlreadyInitialized: true, processTime: 0 };
            }

            if (initializationPromise === null) {
                // We make sure the initialization will be triggered only once per the service life cycle
                initializationPromise = waitScriptToLoad(apiKey);
            }

            return initializationPromise;
        },
        getAllConsents,
        isConsentGiven: (consent: TrackingConsent) => get(consent, getAllConsents()),
        setDefaultConsents: (consents: TrackingConsents) => {
            defaultConsents = consents;
        },
        openPanel: () => {
            const cookieFirstInstance = getCookieFirstInstance();
            if (!cookieFirstInstance) {
                throw new Error('Cookie First not available');
            }

            cookieFirstInstance.openPanel();
        },
        closePanel: () => {
            const cookieFirstInstance = getCookieFirstInstance();
            if (!cookieFirstInstance) {
                return;
            }

            cookieFirstInstance.closePanel();
        },
        /**
         * Subscribe to the changes on {@link TrackingConsents}. This callback won't be called in case the user provided consents in the past.
         * @param observer
         */
        subscribeToConsents: (observer: ConsentsObserver) => consentsObservable.subscribe(observer),
        unsubscribeConsents: (observer: ConsentsObserver) => consentsObservable.unsubscribe(observer),
    };
};

export type TrackingConsentService = ReturnType<typeof createTrackingConsentService>;

export default createTrackingConsentService;
